import { useState, useEffect, useCallback } from 'react';
import type { MarketType } from '@/lib/hyperliquid';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export interface StrategyMetrics {
  tradeCount: number;
  totalPnl: number;
  winRate: number;
  avgPnlPerTrade: number;
  sharpe: number;
  maxLoss: number;
  avgHoldDurationHours: number;
}

export interface StrategyAnalysisData {
  strategies: {
    scalp: StrategyMetrics;
    swing: StrategyMetrics;
    positional: StrategyMetrics;
  };
  tradeCount?: number;
  cached?: boolean;
}

interface UseStrategyAnalysisOptions {
  address: string | undefined;
  rangeDays?: number;
  marketType?: MarketType;
}

interface UseStrategyAnalysisReturn {
  data: StrategyAnalysisData | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useStrategyAnalysis({
  address,
  rangeDays = 30,
  marketType = 'perps',
}: UseStrategyAnalysisOptions): UseStrategyAnalysisReturn {
  const [data, setData] = useState<StrategyAnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStrategyAnalysis = useCallback(async () => {
    if (!address) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/hl-strategy-analysis?address=${encodeURIComponent(address)}&rangeDays=${rangeDays}&marketType=${encodeURIComponent(marketType)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch strategy analysis');
      }

      const strategyData = await response.json();
      setData(strategyData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch strategy analysis';
      setError(message);
      console.error('Strategy analysis fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [address, rangeDays, marketType]);

  useEffect(() => {
    if (address) {
      fetchStrategyAnalysis();
    }
  }, [address, rangeDays, marketType, fetchStrategyAnalysis]);

  return {
    data,
    isLoading,
    error,
    refresh: fetchStrategyAnalysis,
  };
}
